/**
 * FootprintDecalModule — footprints behind the player, other travelers and
 * every walking versona (2026-09-10).
 *
 * "Add a footprint decal that applies to all grounds except when a versona or
 * the user is indoors, and make a specific decal for when it is snowing."
 *
 * The rules are pure (lib/footprints): a print per stride, alternating feet,
 * on every OUTDOOR ground — terrain, roads, plateaus, cave floors — never
 * inside a dwelling / structure / sky-ship / station, never on a ride, under
 * water or in the air. Two decal families, one InstancedMesh each, both a
 * ring buffer of flat quads laid on the ground's slope with a polygon
 * offset: GROUND = a faint dark scuff (24 s), SNOW = a pressed hollow with a
 * blue shadow and a bright rim (80 s) whenever it is snowing or snow lies
 * (WeatherModule's emitter kind / snowLevel). Fade rides a per-instance
 * `aBorn` attribute against a `now` uniform in TSL, so ageing costs no CPU.
 * Textures are canvas-baked soles (no assets). Absolute coordinates under
 * the scene (the FP64 rebase law). Diag `__footprints()`.
 */
import * as THREE from 'three/webgpu';
import { attribute, uniform, texture, float, clamp, vec3 } from 'three/tsl';
import type { App } from 'enginex/core/App.js';
import type { IModule } from 'enginex/core/App.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import type { EnvironmentModule } from './EnvironmentModule.js';
import { waterStillAt, stampWater, waterFx } from '../lib/water_fx.js';
import { strideStamp, swimStrokeStamp, SWIM_DEPTH_M } from '../lib/water_physics.js';
import { newWetness, wetnessStep, printWetness, wetPrintBornShift, StrokeClock, WET_PRINT_LIFE_S, type Wetness } from '../lib/water_interact.js';
import { inverseDistanceGain } from '../lib/sfx.js';
import {
  surfaceFor, printLife, newStepper, advance, FADE_TAIL_S, PRINT_W_M, PRINT_L_M, WALKER_RANGE_M, type Surface, type Stepper,
} from '../lib/footprints.js';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { biomeAt } from '@shared/terrain.js';
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { sfxAt } from '../lib/sfx.js';
import { FootfallBudget, FOOTFALL_RANGE_M, StepClock, footSurfaceFor, footfallGain, playerFootfallGain, playerSteps, type Walker as FootWalker } from '../lib/audio/footfalls.js';
import { audioRuntime } from '../lib/audio/runtime.js';
import { RUN_SPEED_MPS } from '../lib/footprints.js';
import { deformStride } from '../lib/deform_field.js'; // weather surfaces R3: snow and mud that remember
import { getWetness } from '../lib/wetness.js';

const MAX_PER_FAMILY = 400;
const LIFT_M = 0.02;
const WALKER_FORGET_S = 6;

/** A print family: the two grounds' and, since W6 (2026-10-05), the WET print a body leaves for a while after deep water. */
type PrintKind = Surface | 'wet';

interface Family {
  surface: PrintKind;
  mesh: THREE.InstancedMesh;
  born: THREE.InstancedBufferAttribute;
  next: number;
  written: number;
  life: number;
}

interface Walker { st: Stepper; seenAt: number; clock: StepClock; wet: Wetness; stroke: StrokeClock }

export class FootprintDecalModule implements IModule {
  readonly name = 'FootprintDecals';
  private readonly root = new THREE.Group();
  private readonly families = new Map<PrintKind, Family>();
  private readonly walkers = new Map<string, Walker>();
  private player: Stepper | null = null;
  private readonly nowU = uniform(0);
  private t = 0;
  private pruneAcc = 0;
  private app: App | null = null;
  private lastSurface: Surface = 'none';
  private laid = { ground: 0, snow: 0, wet: 0 };
  /** W5 (2026-10-05): my own swim strokes' clock (a crawl is ~1.4 strokes a second, never a footfall per 0.7 m). */
  private readonly myStrokes = new StrokeClock();
  private fieldPrints = 0; // weather surfaces R3: snow prints the deformation field took instead of a decal
  // ── the SOUND of other people's strides (2026-09-20): synthesised footfalls, placed where the foot landed ──
  private readonly footBudget = new FootfallBudget();
  private footSeed: string | null = null;
  private footWorld: bigint | null = null;
  private footSites: SettlementSite[] = [];
  private heard = { traveler: 0, versona: 0, overBudget: 0, lastSurface: '', mine: 0 };
  private readonly myClock = new StepClock();
  private readonly tmpQ = new THREE.Quaternion();
  private readonly tmpQ2 = new THREE.Quaternion();
  private readonly tmpP = new THREE.Vector3();
  private readonly tmpS = new THREE.Vector3();
  private readonly tmpN = new THREE.Vector3();
  private readonly tmpM = new THREE.Matrix4();
  private readonly up = new THREE.Vector3(0, 1, 0);

  constructor(private readonly env: EnvironmentModule) {}

  init(app: App): void {
    this.app = app;
    this.root.name = 'footprint-decals';
    app.world.scene.add(this.root);
    this.families.set('ground', this.buildFamily('ground'));
    this.families.set('snow', this.buildFamily('snow'));
    this.families.set('wet', this.buildFamily('wet'));
    app.world.addSystem(makeSystem('FootprintDecals', Phase.Animation, 42, (dt) => this.frame(dt)));
    (window as unknown as Record<string, unknown>).__footprints = () => ({
      laid: { ...this.laid }, fieldPrints: this.fieldPrints, lastSurface: this.lastSurface, walkers: this.walkers.size,
      live: { ground: this.liveCount('ground'), snow: this.liveCount('snow'), wet: this.liveCount('wet') },
      wet: { ...waterFx().wet }, strokes: waterFx().interact.strokes, remoteStrokes: waterFx().interact.remoteStrokes,
      playerHeight: +(atlas.localY - this.env.groundAt(atlas.localX, atlas.localZ)).toFixed(2),
      lastPrint: this.lastPrint,
      // Probe levers (the AO overlay probe, 2026-09-17): a ring of prints
      // around the player without walking, and where each live print sits on
      // screen (CSS px) so a frame readback can be judged INSIDE the squares.
      heard: { ...this.heard, tokens: +this.footBudget.level.toFixed(2) },
      layAround: (n = 24, r = 2.2) => {
        const s: Surface = this.lastSurface === 'snow' ? 'snow' : 'ground';
        for (let i = 0; i < n; i++) { const a = (i / n) * Math.PI * 2; this.lay(s, atlas.localX + Math.cos(a) * r, atlas.localZ + Math.sin(a) * r, a, i % 2 ? 1 : -1); }
        return n;
      },
      rects: () => this.screenRects(),
    });
  }

  /** Every live print's screen-space box (CSS px) through the first-person camera. */
  private screenRects(): Array<{ x0: number; y0: number; x1: number; y1: number; surface: PrintKind }> {
    const app = this.app; if (!app) return [];
    const cam = atlas.localMesh?.children.find((c) => (c as { isCamera?: boolean }).isCamera) as THREE.PerspectiveCamera | undefined;
    if (!cam) return [];
    const size = app.renderer.native.getSize(new THREE.Vector2());
    const scene = app.world.scene;
    const m = new THREE.Matrix4(); const v = new THREE.Vector3();
    const out: Array<{ x0: number; y0: number; x1: number; y1: number; surface: PrintKind }> = [];
    const corners = [[-PRINT_W_M / 2, 0, -PRINT_L_M / 2], [PRINT_W_M / 2, 0, -PRINT_L_M / 2], [PRINT_W_M / 2, 0, PRINT_L_M / 2], [-PRINT_W_M / 2, 0, PRINT_L_M / 2]];
    for (const f of this.families.values()) {
      for (let i = 0; i < Math.min(f.written, MAX_PER_FAMILY); i++) {
        if (this.t - f.born.getX(i) >= f.life) continue;
        f.mesh.getMatrixAt(i, m);
        let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, ok = true;
        for (const c of corners) {
          // Instances are ABSOLUTE; the camera lives in render space (scene.position = −origin).
          v.set(c[0], c[1], c[2]).applyMatrix4(m).add(scene.position).project(cam);
          if (v.z >= 1 || v.z <= -1) { ok = false; break; }
          const sx = (v.x + 1) * 0.5 * size.x, sy = (1 - v.y) * 0.5 * size.y;
          x0 = Math.min(x0, sx); y0 = Math.min(y0, sy); x1 = Math.max(x1, sx); y1 = Math.max(y1, sy);
        }
        if (!ok || x1 < 0 || y1 < 0 || x0 > size.x || y0 > size.y) continue;
        out.push({ x0: Math.max(0, x0), y0: Math.max(0, y0), x1: Math.min(size.x, x1), y1: Math.min(size.y, y1), surface: f.surface });
      }
    }
    return out;
  }

  private lastPrint: { x: number; y: number; z: number; yaw: number; surface: PrintKind } | null = null;

  private liveCount(surface: PrintKind): number {
    const f = this.families.get(surface);
    if (!f) return 0;
    let n = 0;
    for (let i = 0; i < Math.min(f.written, MAX_PER_FAMILY); i++) if (this.t - f.born.getX(i) < f.life) n++;
    return n;
  }

  private buildFamily(surface: PrintKind): Family {
    const geo = new THREE.PlaneGeometry(PRINT_W_M, PRINT_L_M);
    geo.rotateX(-Math.PI / 2); // flat, length along +z (the heading)
    const born = new THREE.InstancedBufferAttribute(new Float32Array(MAX_PER_FAMILY).fill(-1e9), 1);
    born.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aBorn', born);
    // W6: a WET print dries over WET_PRINT_LIFE_S (a late one is born "older" — `wetPrintBornShift`), drawn darker
    // and stronger than a scuff: water darkens the ground it soaks.
    const life = surface === 'wet' ? WET_PRINT_LIFE_S : printLife(surface);
    const map = soleTexture(surface === 'wet' ? 'ground' : surface);
    const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
    mat.toneMapped = false;
    const sample = texture(map);
    const age = this.nowU.sub(attribute('aBorn', 'float'));
    const fade = clamp(float(life).sub(age).div(FADE_TAIL_S), 0, 1);
    const strength = surface === 'snow' ? 0.85 : surface === 'wet' ? 0.62 : 0.42;
    mat.colorNode = surface === 'snow' ? sample.rgb : surface === 'wet' ? vec3(0.035, 0.032, 0.03) : vec3(0.12, 0.09, 0.06);
    mat.opacityNode = sample.a.mul(fade).mul(strength);
    pinStaticMotionVectors(mat); // prints never move once placed (three's frozen previous instance matrices)
    markMrtOverlay(mat); // the ground under a print keeps its OWN normal — AO shaded every square from the decal's (a mirrored left foot is back-facing, its normal points down: full occlusion)
    const mesh = new THREE.InstancedMesh(geo, mat, MAX_PER_FAMILY);
    mesh.count = 0;
    mesh.frustumCulled = false;
    mesh.renderOrder = 2;
    mesh.name = `footprints-${surface}`;
    this.root.add(mesh);
    return { surface, mesh, born, next: 0, written: 0, life };
  }

  private weatherMod: { getPrecip?(): { kind: string }; snowLevel?: number } | undefined;
  private weather(): { snowing: boolean; snowCover: number } {
    const w = (this.weatherMod ??= this.app?.getModule('Weather') as { getPrecip?(): { kind: string }; snowLevel?: number } | undefined); // cached: a linear module scan per frame otherwise
    return { snowing: w?.getPrecip?.().kind === 'snow', snowCover: w?.snowLevel ?? 0 };
  }

  private frame(dt: number): void {
    this.t += dt;
    this.nowU.value = this.t;
    if (atlas.loadingGate || atlas.currentWorldId === null) return;
    const { snowing, snowCover } = this.weather();
    const px = atlas.localX, pz = atlas.localZ;
    this.footBudget.tick(dt);
    // ── the player ──
    const ground = this.env.groundAt(px, pz);
    const mine = surfaceFor({
      indoors: atlas.interiorId !== null, spaceInterior: atlas.spaceInterior,
      riding: atlas.hoverRide || atlas.skyFlight || atlas.spaceFlight || atlas.flyMode,
      submerged: atlas.submerged, heightAboveGround: atlas.localY - ground, snowing, snowCover,
    });
    this.lastSurface = mine;
    if (!this.player) this.player = newStepper(px, pz);
    const myDist = Math.hypot(px - this.player.lastX, pz - this.player.lastZ);
    const mySpeed = dt > 0 ? myDist / dt : 0;
    const mySteps = advance(this.player, px, pz, dt);
    this.myFootfalls(myDist, mySpeed, this.waterDepth(px, pz, ground));
    // Interactive water: water over the ground here → every stride rings the
    // surface (wading) or strokes it (swimming) instead of leaving a print.
    const myWater = this.waterDepth(px, pz, ground);
    this.myStrokeSounds(dt, mySpeed);
    const myWet = printWetness(waterFx().wet); // W6: PhysicsModule steps my wetness; a fresh climb-out prints wet
    if (myWater > 0) { for (const p of mySteps) this.ring(p.x, p.z, myWater); }
    else if (mine === 'ground' && myWet > 0) for (const p of mySteps) this.lay('wet', p.x, p.z, p.yaw, p.side, myWet);
    else if (mine !== 'none') for (const p of mySteps) this.lay(mine, p.x, p.z, p.yaw, p.side);
    // ── other travelers (their bodies are absolute in the scene) ──
    for (const r of atlas.remotes.values()) {
      if (!r.mesh || r.animState.startsWith('ride:') || r.animState.startsWith('fly:')) continue;
      const rx = r.mesh.position.x, rz = r.mesh.position.z;
      if ((rx - px) ** 2 + (rz - pz) ** 2 > WALKER_RANGE_M * WALKER_RANGE_M) continue;
      const key = `remote:${r.identityHex}`;
      let w = this.walkers.get(key);
      if (!w) { w = { st: newStepper(rx, rz), seenAt: this.t, clock: new StepClock(), wet: newWetness(), stroke: new StrokeClock() }; this.walkers.set(key, w); }
      w.seenAt = this.t;
      const g = this.env.groundAt(rx, rz);
      const surf = surfaceFor({ indoors: false, spaceInterior: false, riding: false, submerged: false, heightAboveGround: r.mesh.position.y - g, snowing, snowCover });
      const rDist = Math.hypot(rx - w.st.lastX, rz - w.st.lastZ), rSpeed = dt > 0 ? rDist / dt : 0;
      const steps = advance(w.st, rx, rz, dt);
      const rWater = this.waterDepth(rx, rz, g);
      const rSwim = rWater >= SWIM_DEPTH_M || r.animState === 'swim';
      wetnessStep(w.wet, rWater, rSwim, dt); // W6: another traveler leaving deep water prints wet too
      // W5: a swimming traveler's strokes are heard (their own clock, the footfall budget's token).
      if (rSwim) { for (let n = w.stroke.advance(dt, rSpeed); n > 0; n--) this.remoteStroke(rx, r.mesh.position.y, rz); }
      else w.stroke.reset();
      // The SOUND keeps its own cadence (StepClock): a print every metre is right for the eye, a machine-gun for the ear.
      if (!rSwim && (surf !== 'none' || rWater > 0)) for (let n = w.clock.advance(rDist, rSpeed); n > 0; n--) this.footfall('traveler', rx, g, rz, surf === 'snow', rSpeed, rWater);
      if (rWater > 0) { for (const p of steps) this.ring(p.x, p.z, rWater); continue; }
      const rWet = printWetness(w.wet);
      if (surf === 'ground' && rWet > 0) for (const p of steps) this.lay('wet', p.x, p.z, p.yaw, p.side, rWet);
      else if (surf !== 'none') for (const p of steps) this.lay(surf, p.x, p.z, p.yaw, p.side);
    }
    // ── walking versonas (outdoors only — the accessor skips residents inside) ──
    for (const v of this.env.walkers(px, pz, WALKER_RANGE_M)) {
      let w = this.walkers.get(v.id);
      if (!w) { w = { st: newStepper(v.x, v.z, Number(v.id.slice(-1)) % 2 === 0 ? 1 : -1), seenAt: this.t, clock: new StepClock(), wet: newWetness(), stroke: new StrokeClock() }; this.walkers.set(v.id, w); }
      w.seenAt = this.t;
      const surf = surfaceFor({ indoors: false, spaceInterior: false, riding: false, submerged: false, heightAboveGround: 0, snowing, snowCover });
      const vDist = Math.hypot(v.x - w.st.lastX, v.z - w.st.lastZ), vSpeed = dt > 0 ? vDist / dt : 0;
      const steps = advance(w.st, v.x, v.z, dt);
      const vWater = this.waterDepth(v.x, v.z, v.y);
      wetnessStep(w.wet, vWater, vWater >= SWIM_DEPTH_M, dt); // W6: a townsperson out of the lake prints wet too
      if (surf !== 'none' || vWater > 0) for (let n = w.clock.advance(vDist, vSpeed); n > 0; n--) this.footfall('versona', v.x, v.y, v.z, surf === 'snow', vSpeed, vWater);
      if (vWater > 0) { for (const p of steps) this.ring(p.x, p.z, vWater); continue; }
      const vWet = printWetness(w.wet);
      if (surf === 'ground' && vWet > 0) for (const p of steps) this.lay('wet', p.x, p.z, p.yaw, p.side, vWet);
      else if (surf !== 'none') for (const p of steps) this.lay(surf, p.x, p.z, p.yaw, p.side);
    }
    this.pruneAcc += dt;
    if (this.pruneAcc > 2) {
      this.pruneAcc = 0;
      for (const [k, w] of this.walkers) if (this.t - w.seenAt > WALKER_FORGET_S) this.walkers.delete(k);
    }
  }

  /**
   * The SOUND of a stride that is not mine: a synthesised footfall AT the foot. Every other walker used to move in
   * silence — the only footsteps in the game were the player's own loop. The surface follows the same rule the
   * player's loop does (snow over everything, paving inside a town, else the biome); the level is this module's own
   * distance curve (the panner adds direction only); a token bucket that prefers the near keeps a plaza of fifty from
   * being a hundred voices a second. My OWN steps stay the recorded loop (AtlasAmbienceModule).
   */
  private footfall(who: FootWalker, x: number, y: number, z: number, snow: boolean, speed: number, waterM = 0): void {
    const d = Math.hypot(x - atlas.localX, y - atlas.localY, z - atlas.localZ);
    const range = FOOTFALL_RANGE_M[who];
    if (d >= range) return;
    // Wading has recorded takes or nothing (no synthesised stand-in): ask for them only when the manifest lists them.
    const wading = waterM > 0.05;
    if (wading && audioRuntime().variantCount('foot_water') === 0) return;
    if (!this.footBudget.take(d / range)) { this.heard.overBudget++; return; }
    const paved = this.pavedAt(x, z); // FIRST: it resolves the world's seed the biome lookup needs
    const surface = wading ? 'water' : footSurfaceFor({ snow, paved, biome: this.footSeed ? biomeAt(this.footSeed, x, z) : null });
    this.heard[who]++;
    this.heard.lastSurface = surface;
    // No per-key cooldown (two walkers may land in the same instant — the key's instance cap and the bus budget bound
    // it); ±7 % of rate so even one variant never lands twice alike. Low priority: the first thing a busy mix sheds.
    sfxAt(`foot_${surface}`, { x, y: y + 0.05, z }, { gain: footfallGain(d, who, speed > RUN_SPEED_MPS * 0.8), rate: 0.93 + Math.random() * 0.14, cooldownMs: 0, bus: 'npcFoley', priority: who === 'traveler' ? 38 : 28 });
  }

  /**
   * W5 (2026-10-05): MY OWN STROKES. Afloat at the surface and moving, a stroke on the crawl's own cadence
   * (`StrokeClock`: ~0.8 → 1.4 a second) — head-locked (they are mine), the synthesised takes through the variants
   * manifest. Under the surface the underwater mix speaks instead; the jump key's stroke is PhysicsModule's.
   */
  private myStrokeSounds(dt: number, speedMps: number): void {
    if (!this.physicsRef) this.physicsRef = (this.app?.getModule('PhysicsModule') as { crouched?: boolean; waterMode?: string } | undefined) ?? null;
    const swimming = this.physicsRef?.waterMode === 'swim' && !atlas.submerged && !atlas.headUnder && atlas.interiorId === null;
    if (!swimming) { this.myStrokes.reset(); return; }
    for (let n = this.myStrokes.advance(dt, speedMps); n > 0; n--) {
      waterFx().interact.strokes++;
      audioRuntime().playKey('sfx_swim_stroke', { gain: 0.3, rate: 0.93 + Math.random() * 0.14, bus: 'playerFoley', priority: 46 });
    }
  }

  /** W5: another traveler's stroke, AT the swimmer (ONE DISTANCE OWNER: this gain owns distance), under the footfall
   *  budget (a lake of swimmers is never a wall of splashes). */
  private remoteStroke(x: number, y: number, z: number): void {
    const d = Math.hypot(x - atlas.localX, y - atlas.localY, z - atlas.localZ);
    const range = FOOTFALL_RANGE_M.traveler;
    if (d >= range || !this.footBudget.take(d / range)) return;
    waterFx().interact.remoteStrokes++;
    sfxAt('sfx_swim_stroke', { x, y, z }, { gain: 0.32 * inverseDistanceGain(d, 3, range), rate: 0.93 + Math.random() * 0.14, cooldownMs: 0, bus: 'npcFoley', priority: 36 });
  }

  /**
   * MY OWN STEPS, as contacts (2026-09-20). When recorded takes exist for the ground under me (`playerSteps().contact`,
   * decided by AtlasAmbienceModule — it owns "may my feet be heard, and on what"), each stride of REAL movement plays
   * one: head-locked (they are mine), on the cadence clock (they follow pace — a crouch creeps, a sprint drums), the
   * shuffle bag choosing the take. Without takes the ambience module's recorded loop plays instead, as it always did.
   */
  private myFootfalls(distM: number, speedMps: number, waterM: number): void {
    const ps = playerSteps();
    if (!ps.onFoot) { this.myClock.reset(); return; }
    const rt = audioRuntime();
    const wading = waterM > 0.05 && rt.variantCount('foot_water') > 0;
    if (!ps.contact && !wading) return;
    // PhysicsModule may register after this module: keep asking until it is there.
    if (!this.physicsRef) this.physicsRef = (this.app?.getModule('PhysicsModule') as { crouched?: boolean } | undefined) ?? null;
    const crouched = this.physicsRef?.crouched === true;
    for (let n = this.myClock.advance(distM, speedMps); n > 0; n--) {
      this.heard.mine++;
      ps.played++;
      rt.playKey(`foot_${wading ? 'water' : this.lastSurface === 'snow' && rt.variantCount('foot_snow') > 0 ? 'snow' : ps.surface}`,
        { gain: playerFootfallGain(speedMps, crouched), rate: 0.94 + Math.random() * 0.12, bus: 'playerFoley', priority: 45 });
    }
  }
  private physicsRef: { crouched?: boolean; waterMode?: string } | null = null;

  /** Paving: inside a settlement's core (the ambience module's own 200 m rule — one town, one ground). */
  private pavedAt(x: number, z: number): boolean {
    const conn = atlas.conn;
    if (!conn || atlas.currentWorldId === null) return false;
    if (this.footWorld !== atlas.currentWorldId || this.footSeed === null) { // a world change re-seeds; a row not landed yet is retried
      let seed: string | null = null;
      for (const w of conn.db.world.iter()) if (w.worldId === atlas.currentWorldId) { seed = w.terrainSeed; break; }
      this.footWorld = atlas.currentWorldId;
      this.footSeed = seed;
      this.footSites = seed && atlas.currentWorldId === atlas.commonsWorldId ? settlementSites(seed) : []; // memoised per seed
    }
    for (const s of this.footSites) if (Math.abs(s.centerX - x) < 200 && Math.abs(s.centerZ - z) < 200 && Math.hypot(s.centerX - x, s.centerZ - z) < 200) return true;
    return false;
  }

  /** Water over the ground at (x, z) in metres (0 = dry or the flag off). */
  private waterDepth(x: number, z: number, groundY: number): number {
    const still = waterStillAt(x, z);
    return still === null ? 0 : Math.max(0, still - groundY);
  }

  /** A stride in water: a wading ring, or a stroke when afloat. */
  private ring(x: number, z: number, depth: number): void {
    const s = depth >= SWIM_DEPTH_M ? swimStrokeStamp(1.5) : strideStamp(depth, 1.5);
    stampWater(x, z, s.strength, s.radius);
  }

  private lay(surface: PrintKind, x: number, z: number, yaw: number, side: 1 | -1, wet01 = 0): void {
    // weather surfaces R3: every stride presses the deformation field; where the FIELD shows a snow print the timed
    // decal stands down (the decals are the fallback where the field is off — never a second system on top). A wet
    // print is a ground print to the field (W6).
    if (deformStride(surface === 'wet' ? 'ground' : surface, x, z, yaw, this.weather().snowCover, getWetness())) { this.fieldPrints++; this.lastPrint = { x, y: this.env.groundAt(x, z), z, yaw, surface }; return; }
    const f = this.families.get(surface);
    if (!f) return;
    const y = this.env.groundAt(x, z);
    // the slope from two extra height samples (cheap: the chunk-grid fast path)
    const e = 0.2;
    const dyx = (this.env.groundAt(x + e, z) - this.env.groundAt(x - e, z)) / (2 * e);
    const dyz = (this.env.groundAt(x, z + e) - this.env.groundAt(x, z - e)) / (2 * e);
    this.tmpN.set(-dyx, 1, -dyz).normalize();
    this.tmpQ.setFromUnitVectors(this.up, this.tmpN);
    this.tmpQ2.setFromAxisAngle(this.up, yaw);
    this.tmpQ.multiply(this.tmpQ2);
    this.tmpP.set(x, y + LIFT_M, z);
    this.tmpS.set(side, 1, 1); // the left foot is the right foot mirrored
    this.tmpM.compose(this.tmpP, this.tmpQ, this.tmpS);
    const i = f.next;
    f.mesh.setMatrixAt(i, this.tmpM);
    // A wet print laid late in the window is born "older" — it dries sooner (W6, `wetPrintBornShift`).
    f.born.setX(i, surface === 'wet' ? this.t - wetPrintBornShift(wet01, f.life, FADE_TAIL_S) : this.t);
    f.next = (i + 1) % MAX_PER_FAMILY;
    f.written++;
    f.mesh.count = Math.min(f.written, MAX_PER_FAMILY);
    f.mesh.instanceMatrix.needsUpdate = true;
    f.born.needsUpdate = true;
    this.laid[surface === 'snow' ? 'snow' : surface === 'wet' ? 'wet' : 'ground']++;
    if (surface === 'wet') waterFx().interact.wetPrints++;
    this.lastPrint = { x, y: y + LIFT_M, z, yaw, surface };
  }

  dispose(): void {
    for (const f of this.families.values()) { this.root.remove(f.mesh); f.mesh.geometry.dispose(); (f.mesh.material as THREE.Material).dispose(); }
    this.families.clear();
    this.walkers.clear();
    this.root.parent?.remove(this.root);
  }
}

/** A canvas-baked sole: heel + ball + toes, soft-edged. Snow carries its own colour (rim light, blue hollow). */
function soleTexture(surface: Surface): THREE.CanvasTexture {
  const w = 64, h = 128;
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const g = cv.getContext('2d')!;
  g.clearRect(0, 0, w, h);
  const sole = (): void => {
    g.beginPath();
    g.ellipse(w * 0.5, h * 0.76, w * 0.26, h * 0.16, 0, 0, Math.PI * 2);   // heel
    g.ellipse(w * 0.5, h * 0.36, w * 0.34, h * 0.2, 0, 0, Math.PI * 2);    // ball
    g.ellipse(w * 0.5, h * 0.55, w * 0.22, h * 0.14, 0, 0, Math.PI * 2);   // arch bridge
    for (let i = 0; i < 5; i++) g.ellipse(w * (0.22 + i * 0.14), h * (0.16 + Math.abs(i - 2) * 0.02), w * 0.06, h * 0.05, 0, 0, Math.PI * 2); // toes
  };
  if (surface === 'snow') {
    // rim light: a slightly larger pale sole under the hollow
    g.save(); g.translate(w * 0.5, h * 0.5); g.scale(1.18, 1.1); g.translate(-w * 0.5, -h * 0.5);
    g.fillStyle = 'rgba(235,242,255,0.9)'; sole(); g.fill(); g.restore();
    const hollow = g.createRadialGradient(w * 0.5, h * 0.5, 4, w * 0.5, h * 0.5, h * 0.5);
    hollow.addColorStop(0, 'rgba(120,150,200,1)');
    hollow.addColorStop(1, 'rgba(150,175,215,0.85)');
    g.fillStyle = hollow; sole(); g.fill();
  } else {
    g.filter = 'blur(1.5px)';
    g.fillStyle = 'rgba(255,255,255,1)'; sole(); g.fill();
    g.filter = 'none';
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}
